import { isEmpty, isNil } from 'lodash-es';
import { useSafeState, useSafeDispatch } from '@strava/react-hooks';
import { useHistory } from 'react-router-dom';
import { devLog } from 'utils/dev-utils';
import { ALERT_TYPES } from 'constants/alerts/alert-types';
import {
  completeProfile,
  facebookFriendsNextStep,
  postDirectPromotionConsent,
  existingAthleteFlowNextStep
} from 'api/onboarding';
import {
  ADD_ALERT,
  CLEAR_ALERT,
  UPDATE_STEP,
  UPDATE_SUGGESTED_FRIENDS
} from '../state/Onboarding.actions';
import {
  INPUT_IDS,
  NEW_ATHLETE_FLOW,
  CATEGORIES,
  EXISTING_ATHLETE_FLOW
} from './onboardingConstants';
import { analyticsPageName, stepToUrl, t } from './onboardingUtils';
import { ACTIONS } from '../components/ActionButton/ActionButton';

const useNavigateToNextStep = ({ state, dispatch, track }) => {
  const [isProcessing, setIsProcessing] = useSafeState(false);
  const [elementName, setElementName] = useSafeState(null);

  const safeDispatch = useSafeDispatch(dispatch);

  const { category, currentStep, currentAthlete } = state;
  const { firstName, lastName, dob, gender } = currentAthlete;
  const { year, month, day } = dob || {};

  const history = useHistory();

  const analyticsForCreateProfileStep = (nextStep, alert) => {
    return {
      athlete_input: {
        first_name: firstName,
        last_name: lastName,
        dob,
        gender
      },
      is_valid_input: isEmpty(alert)
    };
  };

  const validateBirthday = (date) => {
    const { month: m, day: d, year: y } = date;
    const erroredControls = [];
    if (isNil(m)) {
      erroredControls.push('month');
    }
    if (isNil(d)) {
      erroredControls.push('day');
    }
    if (isNil(y)) {
      erroredControls.push('year');
    }
    return erroredControls;
  };

  /**
   * Validates controls on `Create your Profile` step and determines the
   * next step when all values are valid.
   *
   * @returns {string} -next step or null when an error occurs //TODO fumba
   */
  const handleCompleteProfile = async () => {
    if (isEmpty(firstName)) {
      return {
        alert: {
          message: t('create_your_profile.errors.first_name'),
          target: INPUT_IDS.firstName
        }
      };
    }
    if (isEmpty(lastName)) {
      return {
        alert: {
          message: t('create_your_profile.errors.last_name'),
          target: INPUT_IDS.lastName
        }
      };
    }

    const erroredControls = validateBirthday(dob);
    if (!isEmpty(erroredControls)) {
      return {
        alert: {
          message: t('create_your_profile.errors.birthday'),
          target: INPUT_IDS[erroredControls[0]], // focus on first invalid date control
          controls: erroredControls
        }
      };
    }

    if (isEmpty(gender) || gender === 'unset') {
      return {
        alert: {
          message: t('create_your_profile.errors.gender'),
          target: INPUT_IDS.gender
        }
      };
    }

    try {
      const response = await completeProfile({
        data: {
          firstname: firstName,
          lastname: lastName,
          gender,
          dateofbirth: {
            year,
            month,
            day
          }
        }
      });
      const { nextStep, suggestions } = response;
      // save suggested friends to state
      if (!isEmpty(suggestions)) {
        safeDispatch({
          type: UPDATE_SUGGESTED_FRIENDS,
          payload: { suggestions }
        });
      }
      return { nextStep };
    } catch (e) {
      devLog(e);
      return {
        alert: {
          message: t('errors.generic')
        }
      };
    }
  };

  /**
   * Determines next step after facebook:
   * Server sends back 'subscription_trial', 'welcome', or 'direct_promotion'
   * @returns {Object} - nextStep or alert
   */
  const handleFacebookFriendsNextStep = async () => {
    try {
      const response = await facebookFriendsNextStep();
      return { nextStep: response.nextStep };
    } catch (e) {
      devLog(e);
      return {
        alert: {
          message: t('errors.generic')
        }
      };
    }
  };

  const handleDirectPromotionNextStep = async (action) => {
    try {
      const response = await postDirectPromotionConsent({
        action,
        currentStep
      });
      return { nextStep: response.nextStep };
    } catch (e) {
      devLog(e);
      return {
        alert: {
          message: t('errors.generic')
        }
      };
    }
  };

  const handleSubscriptionTrialNextStep = async (action) => {
    if (action === ACTIONS.approved) {
      return { redirectPath: '/try-premium/2017-apple-juice-web' };
    }
    // https://github.com/strava/active/blob/4d99e09e5cd68ff563971474676718520a116762/lib/strava/onboardings/flow2.rb
    return { nextStep: NEW_ATHLETE_FLOW.welcome };
  };

  /**
   * click handler for modal action button -- determines next step and handles analytics
   * @param {Object} arg
   * @param {string} arg.analyticsElement - (optional) string for analytics click event, 'next' by default
   * @param {string} arg.action - (optional) one of ACTIONS, 'next' by default
   * @param {string} arg.permissionType - (optional) one of PERMISSION_TYPES
   */
  const stepForward = async ({
    analyticsElement = 'next',
    action = ACTIONS.next,
    permissionType
  } = {}) => {
    const log = `[useNavigateToNextStep] #stepForward \n\tSTART\t{step: ${currentStep} , action: ${action}}`;
    safeDispatch({ type: CLEAR_ALERT });

    setElementName(analyticsElement);
    setIsProcessing(true);

    let nextStep = null;
    let alert = null;
    let redirectPath = null;

    let stepAnalyticsProperties = {};

    if (category === CATEGORIES.newAthlete) {
      // NEW ATHLETE FLOW
      switch (currentStep) {
        case NEW_ATHLETE_FLOW.createProfile: {
          // next step - suggested_friends or facebook_friends
          ({ alert, nextStep } = await handleCompleteProfile());
          stepAnalyticsProperties = analyticsForCreateProfileStep(
            nextStep,
            alert
          );
          break;
        }
        case NEW_ATHLETE_FLOW.suggestedFriends: {
          // next step - facebook_friends
          // https://github.com/strava/active/blob/4d99e09e5cd68ff563971474676718520a116762/lib/strava/onboardings/flow2.rb
          nextStep = NEW_ATHLETE_FLOW.facebookFriends;
          break;
        }
        case NEW_ATHLETE_FLOW.facebookFriends: {
          // next step - subscription_trial, direct_promotion or welcome
          // TODO another potential step - summit_promotion_redeem (?) https://strava.atlassian.net/browse/ACTN-421
          ({ alert, nextStep } = await handleFacebookFriendsNextStep());
          break;
        }
        // next step - welcome or redirect-> subscriptions page
        case NEW_ATHLETE_FLOW.subscriptionTrial: {
          ({
            alert,
            nextStep,
            redirectPath
          } = await handleSubscriptionTrialNextStep(action));
          break;
        }
        // next step - subscription_trial, welcome or summit_promotion_redeem (?)
        // TODO another potential step - summit_promotion_redeem (?) https://strava.atlassian.net/browse/ACTN-421
        case NEW_ATHLETE_FLOW.directPromotion: {
          ({ alert, nextStep } = await handleDirectPromotionNextStep(action));
          break;
        }
        default: {
          // noop
        }
      }
    } else if (category === CATEGORIES.existingAthlete) {
      // EXISTING ATHLETE FLOW

      if (
        currentStep === EXISTING_ATHLETE_FLOW.health &&
        action === ACTIONS.denied
      ) {
        nextStep = EXISTING_ATHLETE_FLOW.health_denial_one;
      } else if (
        currentStep === EXISTING_ATHLETE_FLOW.health_denial_one &&
        action === ACTIONS.back
      ) {
        nextStep = EXISTING_ATHLETE_FLOW.health;
      } else if (
        currentStep === EXISTING_ATHLETE_FLOW.health_denial_one &&
        action === ACTIONS.denied
      ) {
        nextStep = EXISTING_ATHLETE_FLOW.health_denial_two;
      } else if (
        currentStep === EXISTING_ATHLETE_FLOW.health_denial_two &&
        action === ACTIONS.back
      ) {
        nextStep = EXISTING_ATHLETE_FLOW.health;
      } else {
        try {
          const response = await existingAthleteFlowNextStep({
            step: currentStep,
            action,
            permissionType
          });
          nextStep = response.nextStep;

          // temporarily redirect age-confirmation step to
          // onboarding > create profile to allow athletes to
          // update their DOB
          // See https://strava.atlassian.net/browse/ACTN-271 for more
          // details.
          if (nextStep === EXISTING_ATHLETE_FLOW.age_confirmation) {
            redirectPath = '/onboarding/continue';
          }
        } catch (e) {
          alert = {
            message: t('errors.generic')
          };
        }
      }
    }

    track({
      page: analyticsPageName(currentStep),
      action: 'click',
      element: analyticsElement,
      properties: {
        ...stepAnalyticsProperties
      }
    });

    if (isEmpty(alert)) {
      if (isEmpty(nextStep) || !isNil(redirectPath)) {
        devLog(
          log.concat(
            `\n\tEND\t{step: ${nextStep}, redirectPath: ${redirectPath}}`
          )
        );
        // end new and existing athlete flows when the next step is not defined
        // re-route to a specified path if specified

        // This page refresh is necessary when the athlete hits the back button -
        // rails controller endpoints are triggered to re-route the user back to `onboarding/continue`.
        // Without it, the back button will just pop the url from react-router and
        // update the address but it will not re-open the modal.
        window.location.href = redirectPath || '/onboarding';
        return;
      }
      // navigate to next defined step
      safeDispatch({
        type: UPDATE_STEP,
        payload: { nextStep }
      });
      history.push(stepToUrl({ category, step: nextStep }));
    } else {
      // an error occurs while attempting to navigate to next step
      safeDispatch({
        type: ADD_ALERT,
        payload: {
          severity: ALERT_TYPES.error,
          ...alert
        }
      });
    }

    setIsProcessing(false);
    devLog(log.concat(`\n\tEND\t{step: ${nextStep}}`));
  };

  return { stepForward, isProcessing, elementName };
};

export default useNavigateToNextStep;
